import { PaymentEvent, PaymentStatus } from '../types';

/**
 * ─── Machine d'états d'une tentative de paiement ────────────────────────────
 *
 * Machine pure (aucun effet de bord) : `transition(état, événement)` retourne
 * le nouvel état, ou null si la transition est interdite. Les hooks/pages
 * l'utilisent pour garantir qu'aucun raccourci illégal n'est possible
 * (ex: passer de CANCELLED à SUCCESS sans repasser par INITIATE).
 *
 *   IDLE ──INITIATE──▶ LOADING ──REDIRECT──▶ REDIRECTING ─(retour provider)─▶ VERIFYING
 *                        │  └────PUSH_SENT──▶ PENDING ──VERIFY──▶ VERIFYING
 *                        └──NETWORK_FAIL──▶ NETWORK_ERROR ──RETRY──▶ LOADING
 *   VERIFYING ──VERIFIED_OK──▶ SUCCESS
 *   VERIFYING ──VERIFIED_KO──▶ FAILED          FAILED/CANCELLED/… ──RETRY──▶ LOADING
 *   VERIFYING ──TIMEOUT─────▶ EXPIRED          tout état ──RESET──▶ IDLE
 */

type TransitionTable = Partial<
  Record<PaymentStatus, Partial<Record<PaymentEvent, PaymentStatus>>>
>;

const TRANSITIONS: TransitionTable = {
  [PaymentStatus.IDLE]: {
    [PaymentEvent.INITIATE]: PaymentStatus.LOADING,
  },
  [PaymentStatus.LOADING]: {
    [PaymentEvent.REDIRECT]: PaymentStatus.REDIRECTING,
    [PaymentEvent.PUSH_SENT]: PaymentStatus.PENDING,
    [PaymentEvent.VERIFIED_KO]: PaymentStatus.FAILED,
    [PaymentEvent.NETWORK_FAIL]: PaymentStatus.NETWORK_ERROR,
    [PaymentEvent.CANCEL]: PaymentStatus.CANCELLED,
  },
  [PaymentStatus.REDIRECTING]: {
    [PaymentEvent.RETURNED_APPROVED]: PaymentStatus.VERIFYING,
    [PaymentEvent.RETURNED_CANCELLED]: PaymentStatus.CANCELLED,
    [PaymentEvent.RETURNED_DECLINED]: PaymentStatus.FAILED,
    [PaymentEvent.TIMEOUT]: PaymentStatus.EXPIRED,
    [PaymentEvent.NETWORK_FAIL]: PaymentStatus.NETWORK_ERROR,
  },
  [PaymentStatus.PENDING]: {
    [PaymentEvent.VERIFY]: PaymentStatus.VERIFYING,
    [PaymentEvent.CANCEL]: PaymentStatus.CANCELLED,
    [PaymentEvent.TIMEOUT]: PaymentStatus.EXPIRED,
    [PaymentEvent.NETWORK_FAIL]: PaymentStatus.NETWORK_ERROR,
  },
  [PaymentStatus.VERIFYING]: {
    [PaymentEvent.VERIFIED_OK]: PaymentStatus.SUCCESS,
    [PaymentEvent.VERIFIED_KO]: PaymentStatus.FAILED,
    [PaymentEvent.TIMEOUT]: PaymentStatus.EXPIRED,
    [PaymentEvent.NETWORK_FAIL]: PaymentStatus.NETWORK_ERROR,
    // Le retour peut être "approved" mais le callback pas encore arrivé :
    // VERIFY re-boucle (polling) tant que le statut réel est pending.
    [PaymentEvent.VERIFY]: PaymentStatus.VERIFYING,
  },
  [PaymentStatus.FAILED]: {
    [PaymentEvent.RETRY]: PaymentStatus.LOADING,
  },
  [PaymentStatus.CANCELLED]: {
    [PaymentEvent.RETRY]: PaymentStatus.LOADING,
  },
  [PaymentStatus.EXPIRED]: {
    [PaymentEvent.RETRY]: PaymentStatus.LOADING,
    // Un paiement "expiré" côté UI peut avoir abouti côté provider :
    // on autorise une re-vérification manuelle.
    [PaymentEvent.VERIFY]: PaymentStatus.VERIFYING,
  },
  [PaymentStatus.NETWORK_ERROR]: {
    [PaymentEvent.RETRY]: PaymentStatus.LOADING,
    [PaymentEvent.VERIFY]: PaymentStatus.VERIFYING,
  },
  [PaymentStatus.SUCCESS]: {},
};

/** États depuis lesquels l'utilisateur peut relancer un paiement. */
export const RETRIABLE_STATUSES: readonly PaymentStatus[] = [
  PaymentStatus.FAILED,
  PaymentStatus.CANCELLED,
  PaymentStatus.EXPIRED,
  PaymentStatus.NETWORK_ERROR,
];

/** États terminaux d'une tentative. */
export const TERMINAL_STATUSES: readonly PaymentStatus[] = [
  PaymentStatus.SUCCESS,
  PaymentStatus.FAILED,
  PaymentStatus.CANCELLED,
  PaymentStatus.EXPIRED,
];

export function transition(
  current: PaymentStatus,
  event: PaymentEvent,
): PaymentStatus | null {
  if (event === PaymentEvent.RESET) return PaymentStatus.IDLE;
  return TRANSITIONS[current]?.[event] ?? null;
}

export function canRetry(status: PaymentStatus): boolean {
  return RETRIABLE_STATUSES.includes(status);
}

export function isTerminal(status: PaymentStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}
